/**
 * Solar Savings Calculator - clean form-first wizard.
 *
 * Flow (11 slides):
 *   1  Postcode
 *   2  Property type
 *   3  Bedrooms
 *   4  Occupants
 *   5  Usage confirm  (auto-estimate + optional exact override)
 *   6  Panel count    (default by property; drives kWp + price)
 *   7  Add-ons        (battery opt-in + EV / diverter / bird mesh)
 *   8  Contact        (name, phone, email)
 *   9  Graph: grid only  (25-year cost, no solar)
 *  10  Graph: with solar (both lines, shaded savings)
 *  11  Results        (headline savings, payback, book-a-call CTA)
 *
 * All numbers come from src/lib/solar-calc.ts - edit that file to tune the
 * calculator without touching this UI.
 */
import { createFileRoute, useSearch } from "@tanstack/react-router";
import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useReducedMotion,
} from "motion/react";
import {
  ArrowLeft,
  ArrowRight,
  Award,
  Battery,
  Bed,
  Bird,
  Bolt,
  Building,
  Building2,
  Check,
  Clock,
  FileCheck,
  Home,
  Hotel,
  Info,
  Mail,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
  Sun,
  TrendingUp,
  Users,
  Warehouse,
  Zap,
} from "lucide-react";
import { z } from "zod";
import crsLogo from "@/assets/crs-logo.webp";
import { ACCREDITATION_LOGOS } from "@/lib/site";
import {
  captureAttribution,
  flushLeadOnUnload,
  getOrCreateSessionId,
  insertLead,
  updateLead,
  type CalculatorLeadPatch,
} from "@/lib/calculator-leads";
import { resolvePrefill, type PrefillSnapshot } from "@/lib/prefill-client";
import { PrefillChip, PREFILLED_INPUT_CLASS } from "@/components/solar/prefill-field-chip";
import {
  PrefillConfirmPanel,
  type ConfirmedPrefill,
} from "@/components/solar/prefill-confirm-panel";
import {
  ensurePixelLoaded,
  readMetaCookies,
  trackBatteryAdded,
  trackCalcStart,
  trackCalcStep,
  trackContactClick,
  trackLead,
  trackPageView,
  trackScheduleClick,
} from "@/lib/calculator-meta";
import {
  BATTERY_OPTION,
  DEFAULT_PANELS_BY_PROPERTY,
  MAX_PANELS,
  MIN_PANELS,
  annualGeneration,
  buildProjection,
  estimateAnnualKwh,
  gbp,
  kwpForPanels,
  outwardArea,
  rateForPostcode,
  regionForPostcode,
  solarPriceForPanels,
  type AddonId,
  type PropertyType,
} from "@/lib/solar-calc";
import { BillScanner } from "@/components/solar/bill-scanner";
import { cn } from "@/lib/utils";
import { getTrafficSource } from "@/lib/traffic-source";

/* -----------------------------------------------------------------------
 * Static config
 * ----------------------------------------------------------------------- */
const PHONE_NUMBER = "01656 339844";
const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/tYUdgJZuJTGoOKkxvz9k";
const WEBHOOK_URL = "/api/public/solar-calculator-webhook";
const SERVICE_AREA_PREFIXES = ["CF", "SA", "NP", "LD"] as const;
const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

/**
 * Customers type numbers in many shapes (+44, 0044, spaces, brackets) and some
 * give a landline. Normalise to 0-prefixed national format and accept any valid
 * UK number, so a good lead is never blocked by formatting.
 */
function normalisePhone(raw: string): string {
  let d = raw.replace(/[^\d+]/g, "");
  if (d.startsWith("+44")) d = `0${d.slice(3)}`;
  else if (d.startsWith("0044")) d = `0${d.slice(4)}`;
  else if (d.startsWith("44") && d.length >= 12) d = `0${d.slice(2)}`;
  return d.replace(/\+/g, "");
}

function isValidUkPhone(raw: string): boolean {
  return /^0\d{9,10}$/.test(normalisePhone(raw));
}

const PROPERTY_OPTIONS: {
  id: PropertyType;
  label: string;
  blurb: string;
  icon: typeof Home;
}[] = [
  { id: "detached", label: "Detached", blurb: "Standalone", icon: Home },
  { id: "semi", label: "Semi-detached", blurb: "Shares one wall", icon: Building },
  { id: "terraced", label: "Terraced", blurb: "In a row", icon: Building2 },
  { id: "bungalow", label: "Bungalow", blurb: "Single-storey", icon: Warehouse },
  { id: "flat", label: "Flat", blurb: "Apartment", icon: Hotel },
];

const ADDONS: { id: AddonId; label: string; blurb: string; icon: typeof Bolt }[] = [
  {
    id: "ev_charger",
    label: "EV Charger",
    blurb: "7 kW smart charger fitted the same visit",
    icon: Bolt,
  },
  {
    id: "diverter",
    label: "Hot Water Diverter",
    blurb: "Sends surplus solar to your immersion heater",
    icon: Zap,
  },
  {
    id: "bird_proofing",
    label: "Bird / Pigeon Proofing",
    blurb: "Mesh skirt around the array from day one",
    icon: Bird,
  },
];

/* -----------------------------------------------------------------------
 * Route
 * ----------------------------------------------------------------------- */
const searchSchema = z
  .object({
    fn: z.string().optional(),
    e: z.string().optional(),
    p: z.string().optional(),
    pc: z.string().optional(),
    src: z.string().optional(),
    cid: z.string().optional(),
    // Opaque personalised-link token from an SMS send. Carries no PII itself.
    k: z.string().optional(),
  })
  .partial();

export const Route = createFileRoute("/solar-savings-calculator")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Solar Savings Calculator | 25-Year Projection | CRS Energy" },
      {
        name: "description",
        content:
          "Answer a few quick questions and see what solar could save you over 25 years. No home visit needed.",
      },
      { property: "og:title", content: "Solar Savings Calculator | 25-Year Projection | CRS Energy" },
      {
        property: "og:description",
        content:
          "Answer a few quick questions and see what solar could save you over 25 years. No home visit needed.",
      },
      { property: "og:type", content: "website" },
      { name: "robots", content: "index, follow" },
    ],
  }),
  component: SolarSavingsCalculatorPage,
});

/* -----------------------------------------------------------------------
 * State
 * ----------------------------------------------------------------------- */
interface WizardState {
  postcode: string;
  houseNumber: string;
  outOfArea: boolean;
  property: PropertyType | null;
  bedrooms: number | null;
  occupants: number | null;
  useEstimate: boolean;
  /** Does the customer know their energy usage? */
  knowsUsage: boolean | null;
  overrideKwh: number | null;
  overrideAnnualCost: number | null;
  /** Does the customer already know how many panels they want? */
  knowsPanels: boolean | null;
  panels: number | null;
  hasBattery: boolean;
  batteryCount: number;
  addons: AddonId[];
  firstName: string;
  phone: string;
  email: string;
  consent: boolean;
  /** Fields that arrived pre-filled from a personalised SMS link. */
  prefilled: Record<PrefillFieldKey, boolean>;
}

export type PrefillFieldKey = "postcode" | "firstName" | "phone" | "email";

const NO_PREFILL: Record<PrefillFieldKey, boolean> = {
  postcode: false,
  firstName: false,
  phone: false,
  email: false,
};

const initialState = (search: z.infer<typeof searchSchema>): WizardState => ({
  postcode: search.pc ?? "",
  houseNumber: "",
  outOfArea: false,
  property: null,
  bedrooms: null,
  occupants: null,
  useEstimate: true,
  knowsUsage: null,
  overrideKwh: null,
  overrideAnnualCost: null,
  knowsPanels: null,
  panels: null,
  hasBattery: true,
  batteryCount: 1,
  addons: [],
  firstName: search.fn ?? "",
  phone: search.p ?? "",
  email: search.e ?? "",
  consent: false,
  prefilled: { ...NO_PREFILL },
});


function derivedAnnualKwh(s: WizardState): number {
  if (!s.useEstimate) {
    if (s.overrideKwh && s.overrideKwh > 0) return s.overrideKwh;
    if (s.overrideAnnualCost && s.overrideAnnualCost > 0) {
      return Math.round(s.overrideAnnualCost / rateForPostcode(s.postcode));
    }
  }
  if (!s.property || !s.bedrooms || !s.occupants) return 0;
  return estimateAnnualKwh(s.property, s.bedrooms, s.occupants);
}

function toLeadPatch(s: WizardState): CalculatorLeadPatch {
  const region = s.postcode ? regionForPostcode(s.postcode) : null;
  const kwh = derivedAnnualKwh(s);
  const patch: CalculatorLeadPatch = {
    postcode: s.postcode || null,
    house_number: s.houseNumber.trim() || null,
    district: region ? String(region) : null,
    out_of_area: s.outOfArea,
    property_type: s.property,
    panel_count: s.panels,
    has_battery: s.hasBattery,
    addons: s.addons,
    annual_usage_kwh: kwh || null,
    usage_source: s.useEstimate ? "derived" : "bill-scan-edited",
    unit_rate_used: s.postcode ? rateForPostcode(s.postcode) : null,
    unit_rate_source: "regional-cap",
    first_name: s.firstName || null,
    email: s.email || null,
    phone: s.phone || null,
  };
  if (s.panels && kwh && s.postcode) {
    const projection = buildProjection({
      annualUsageKwh: kwh,
      unitRate: rateForPostcode(s.postcode),
      panels: s.panels,
      hasBattery: s.hasBattery,
      addonIds: s.addons,
      batteryCount: s.batteryCount,
    });
    patch.system_price = projection.systemPrice;
    patch.savings25 = projection.lifetimeSaving;
    patch.break_even_year = projection.paybackYears;
  }
  return patch;
}

const TOTAL_SLIDES = 10;
/** Contact details are asked for right after the usage step. */
const STEP_CONTACT = 9;

/* -----------------------------------------------------------------------
 * Page
 * ----------------------------------------------------------------------- */
function SolarSavingsCalculatorPage() {
  const search = useSearch({ from: Route.id }) as z.infer<typeof searchSchema>;
  const [state, setState] = useState<WizardState>(() => initialState(search));
  const [step, setStep] = useState(1);

  const stateRef = useRef(state);
  stateRef.current = state;
  const sessionIdRef = useRef<string>("");
  const initialisedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submittedRef = useRef(false);

  const flushSave = useCallback((patch?: CalculatorLeadPatch) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    void updateLead(sid, { ...toLeadPatch(stateRef.current), ...(patch ?? {}) });
  }, []);

  const scheduleSave = useCallback(
    (patch?: CalculatorLeadPatch) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => flushSave(patch), 400);
    },
    [flushSave],
  );

  const skipAddonsRef = useRef(false);
  const update = useCallback(
    (patch: Partial<WizardState>) => {
      if ("knowsPanels" in patch) skipAddonsRef.current = patch.knowsPanels === false;
      setState((s) => ({ ...s, ...patch }));
      scheduleSave();
    },
    [scheduleSave],
  );


  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;
    const sid = getOrCreateSessionId();
    sessionIdRef.current = sid;
    const attr = captureAttribution(search.src ?? "calculator");
    const meta = readMetaCookies(attr.fbclid);
    const traffic = getTrafficSource();
    ensurePixelLoaded();
    trackPageView();
    void insertLead(sid, {
      slide_reached: 1,
      status: "in_progress",
      ...toLeadPatch(stateRef.current),
      lead_source: attr.lead_source,
      page_url: attr.page_url,
      // Acquisition source travels with the lead, so drop-offs can be attributed.
      utm: {
        ...(attr.utm ?? {}),
        traffic_source: traffic.source ?? "",
        traffic_medium: traffic.medium ?? "",
        traffic_campaign: traffic.campaign ?? "",
        traffic_referrer: traffic.referrer ?? "",
        landing_page: traffic.landingPage ?? "",
        arrived_at: new Date().toISOString(),
      },
      fbclid: attr.fbclid,
      fbp: meta.fbp,
      fbc: meta.fbc,
      user_agent: attr.user_agent,
    });
  }, [search.src]);

  // Personalised SMS link: exchange the opaque token for the stored snapshot.
  // We never seed silently - the customer sees one confirmation screen first.
  const prefillDoneRef = useRef(false);
  const [pendingPrefill, setPendingPrefill] = useState<PrefillSnapshot | null>(null);
  useEffect(() => {
    const token = search.k;
    if (!token || prefillDoneRef.current) return;
    prefillDoneRef.current = true;
    let cancelled = false;
    void resolvePrefill(token).then((snap) => {
      if (!snap || cancelled) return;
      setPendingPrefill(snap);
    });
    return () => {
      cancelled = true;
    };
  }, [search.k]);

  const applyPrefill = useCallback(
    (value: ConfirmedPrefill) => {
      setState((s) => ({
        ...s,
        postcode: value.postcode || s.postcode,
        firstName: value.name ? (value.name.split(" ")[0] ?? value.name) : s.firstName,
        phone: value.phone || s.phone,
        email: value.email || s.email,
        prefilled: {
          postcode: Boolean(value.postcode) && value.untouched.postcode,
          firstName: Boolean(value.name) && value.untouched.name,
          phone: Boolean(value.phone) && value.untouched.phone,
          email: Boolean(value.email) && value.untouched.email,
        },
      }));
      setPendingPrefill(null);
      scheduleSave();
      void sendPrefillConfirmWebhook(value).catch((err) =>
        console.error("[solar-savings-calculator] confirm webhook failed", err),
      );
    },
    [scheduleSave],
  );




  useEffect(() => {
    flushSave({ slide_reached: step });
    trackCalcStep(step);
  }, [step, flushSave]);

  useEffect(() => {
    const handler = () => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      flushLeadOnUnload(sid, { ...toLeadPatch(stateRef.current), slide_reached: step });
    };
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, [step]);

  const directionRef = useRef(1);
  const [confirming, setConfirming] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reduceMotionForConfirm = useReducedMotion();

  // Confirm choreography: tick appears -> next slide mounts underneath ->
  // tick rises and fades away, revealing the new header settled in place.
  const CONFIRM_ADVANCE_MS = 550;
  const CONFIRM_TOTAL_MS = 1150;

  // When the customer says they don't know how many panels they want, we skip
  // both the panel counter and the battery / add-ons slide.
  const nextStepFrom = (s: number) => {
    let n = Math.min(s + 1, TOTAL_SLIDES);
    if (skipAddonsRef.current && n === 7) n = 8;
    return n;
  };
  const prevStepFrom = (s: number) => {
    let n = Math.max(1, s - 1);
    if (skipAddonsRef.current && n === 7) n = 6;
    return n;
  };

  const goNext = useCallback(() => {
    directionRef.current = 1;
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    if (reduceMotionForConfirm) {
      setStep((s) => nextStepFrom(s));
      return;
    }
    setConfirming(true);
    advanceTimerRef.current = setTimeout(() => {
      setStep((s) => nextStepFrom(s));
    }, CONFIRM_ADVANCE_MS);
    confirmTimerRef.current = setTimeout(() => {
      setConfirming(false);
    }, CONFIRM_TOTAL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotionForConfirm]);

  const goBack = () => {
    directionRef.current = -1;
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    setConfirming(false);
    setStep((s) => prevStepFrom(s));
  };


  useEffect(() => () => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
  }, []);

  // Fire the lead webhook once, as soon as the customer leaves the Contact slide.
  useEffect(() => {
    if (submittedRef.current) return;
    if (step <= STEP_CONTACT) return;
    if (!state.firstName || !isValidUkPhone(state.phone)) return;
    submittedRef.current = true;
    // Advanced matching only covers values the customer typed themselves.
    // Anything still flagged as pre-filled from an SMS link is withheld, so
    // resolved PII never reaches Meta/GA payloads.
    trackLead(
      {
        email: state.prefilled.email ? undefined : state.email,
        phone: state.prefilled.phone ? undefined : state.phone,
        firstName: state.prefilled.firstName ? undefined : state.firstName,
      },
      { value: 0, currency: "GBP", content_name: "solar-savings-calculator" },
    );
    flushSave({
      status: "submitted",
      consent_marketing: state.consent,
      consent_at: state.consent ? new Date().toISOString() : undefined,
    });
    void sendWebhook(state).catch((err) =>
      console.error("[solar-savings-calculator] webhook failed", err),
    );
  }, [step, state, flushSave]);

  const projection = useMemo(() => {
    const kwh = derivedAnnualKwh(state);
    if (!kwh || !state.panels) return null;
    return buildProjection({
      annualUsageKwh: kwh,
      unitRate: rateForPostcode(state.postcode),
      panels: state.panels,
      hasBattery: state.hasBattery,
      addonIds: state.addons,
      batteryCount: state.batteryCount,
    });
  }, [state]);

  const reduceMotion = useReducedMotion();
  const slideDuration = reduceMotion ? 0 : 0.5;
  const slideVariants = {
    enter: (dir: number) =>
      reduceMotion ? { opacity: 1, y: 0 } : { y: dir > 0 ? 18 : -18, opacity: 0 },
    center: { y: 0, opacity: 1 },
    exit: (dir: number) =>
      reduceMotion ? { opacity: 0, y: 0 } : { y: dir > 0 ? -12 : 12, opacity: 0 },
  };

  const [nextReg, setNextReg] = useState<NextReg | null>(null);

  return (
    <NextNavContext.Provider value={{ reg: nextReg, setReg: setNextReg }}>
    <div className="min-h-[100dvh] flex flex-col text-foreground relative bg-gradient-to-br from-muted/40 via-background to-accent/[0.04]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(60% 40% at 10% 0%, hsl(var(--accent) / 0.10), transparent 60%), radial-gradient(50% 40% at 100% 100%, hsl(var(--primary) / 0.08), transparent 60%)",
        }}
      />
      <TopBar />
      {!pendingPrefill && <ProgressStrip current={step} total={TOTAL_SLIDES} />}
      <main className="flex-1 min-h-0 flex items-start md:items-center justify-center px-3 py-2 md:py-10">
        <div className="w-full max-w-lg relative">
          {pendingPrefill ? (
            <PrefillConfirmPanel
              snapshot={pendingPrefill}
              onConfirm={applyPrefill}
              onReject={() => setPendingPrefill(null)}
            />
          ) : (
          <AnimatePresence mode="wait" custom={directionRef.current}>
            <motion.div
              key={step}
              custom={directionRef.current}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: slideDuration, ease: "easeOut" }}
            >
              {step === 1 && (
                <SlidePostcode
                  state={state}
                  update={update}
                  onNext={() => {
                    trackCalcStart();
                    goNext();
                  }}
                />
              )}
              {step === 2 && (
                <SlideProperty state={state} update={update} onNext={goNext} onBack={goBack} />
              )}
              {step === 3 && (
                <SlideHouseNumber state={state} update={update} onNext={goNext} onBack={goBack} />
              )}
              {step === 4 && (
                <SlideBedrooms state={state} update={update} onNext={goNext} onBack={goBack} />
              )}
              {step === 5 && (
                <SlideOccupants state={state} update={update} onNext={goNext} onBack={goBack} />
              )}
              {step === 6 && (
                <SlidePanels state={state} update={update} onNext={goNext} onBack={goBack} />
              )}
              {step === 7 && (
                <SlideAddons state={state} update={update} onNext={goNext} onBack={goBack} />
              )}
              {step === 8 && (
                <SlideUsage
                  state={state}
                  update={update}
                  onNext={goNext}
                  onBack={goBack}
                  sessionId={sessionIdRef.current}
                />
              )}
              {step === STEP_CONTACT && (
                <SlideContact state={state} update={update} onNext={goNext} onBack={goBack} />
              )}
              {step === 10 && projection && state.panels && (
                <SlideResults
                  projection={projection}
                  firstName={state.firstName}
                  state={state}
                  onBack={goBack}
                />
              )}

            </motion.div>
          </AnimatePresence>
          )}
          <AnimatePresence>
            {confirming && <SlideConfirm key="confirm" />}
          </AnimatePresence>
        </div>
      </main>
      <BottomBar />
    </div>
    </NextNavContext.Provider>
  );
}

type NextReg = { onNext?: () => void; nextLabel: string; nextDisabled?: boolean; hoverArrow?: boolean };
const NextNavContext = createContext<{
  reg: NextReg | null;
  setReg: (r: NextReg | null) => void;
}>({ reg: null, setReg: () => {} });

/* -----------------------------------------------------------------------
 * Chrome
 * ----------------------------------------------------------------------- */
function TopBar() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 text-foreground shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/90">
      <div className="container-page flex h-16 items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={crsLogo}
            alt="CRS Energy"
            width={900}
            height={345}
            className="h-8 md:h-9 w-auto shrink-0"
            decoding="async"
          />
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="https://uk.trustpilot.com/review/crsenergy.org"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border bg-card text-foreground px-2.5 py-1 text-[11px] font-semibold shadow-sm hover:bg-muted transition-colors"
            aria-label="View CRS Energy on Trustpilot, 4.6 out of 5"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden>
              <rect width="24" height="24" fill="#00B67A" rx="2" />
              <path
                fill="#fff"
                d="M12 3.5l2.2 5.1 5.55.45-4.22 3.64 1.3 5.41L12 15.27l-4.83 2.83 1.3-5.41-4.22-3.64L9.8 8.6 12 3.5z"
              />
            </svg>
            <span className="font-semibold tracking-tight">Trustpilot</span>
            <span className="tabular-nums font-bold">4.6</span>
            <span className="inline-flex items-center gap-0.5" aria-hidden>
              {[0, 1, 2, 3, 4].map((i) => (
                <svg key={i} viewBox="0 0 20 20" className="h-3 w-3" fill="#00B67A">
                  <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.8L10 14.9l-5.2 2.8 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
                </svg>
              ))}
            </span>
          </a>
          <a
            href={`tel:${PHONE_NUMBER.replace(/\s+/g, "")}`}
            onClick={() => trackContactClick()}
            className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-3.5 py-2 text-sm font-semibold transition-colors"
          >
            <Phone className="h-4 w-4 group-hover:animate-[nudge-right_1.2s_ease-in-out_infinite]" aria-hidden />
            <span className="hidden sm:inline">{PHONE_NUMBER}</span>
            <span className="sm:hidden">Call</span>
          </a>
        </div>
      </div>
      <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-accent to-transparent" aria-hidden />
    </header>
  );
}

function BottomBar() {
  return (
    <footer className="border-t border-border/60 bg-background/80 backdrop-blur">
      <div className="container-page py-3">
        <div className="hidden md:flex items-center gap-2 justify-center mb-2">
          <span className="h-px w-8 bg-border" />
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Accredited & Certified
          </p>
          <span className="h-px w-8 bg-border" />
        </div>
        <div
          className="relative overflow-hidden hidden md:block"
          style={{
            maskImage: "linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%)",
          }}
          aria-label="Accreditations"
        >
          <div className="flex gap-10 animate-[accreditations-scroll_28s_linear_infinite] w-max">
            {[0, 1].map((loop) => (
              <div key={loop} className="flex items-center gap-10 shrink-0">
                {ACCREDITATION_LOGOS.map((logo, i) => (
                  <img
                    key={`${loop}-${i}`}
                    src={logo.src}
                    alt={logo.alt}
                    className="h-8 w-auto max-w-[7rem] object-contain grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all"
                    loading="lazy"
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-1 text-[11px] text-muted-foreground/80">
          <p>© {new Date().getFullYear()} Complete Remedial Solutions (CRS) Ltd</p>
          <nav className="flex gap-3">
            <a href="/privacy" className="hover:text-foreground">Privacy</a>
            <a href="/data-protection" className="hover:text-foreground">Terms</a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

const PHASES = [
  { label: "About You", upTo: 5 },
  { label: "Your Home", upTo: 8 },
  { label: "Your Estimate", upTo: 11 },
] as const;

function ProgressStrip({ current, total }: { current: number; total: number }) {
  const reduce = useReducedMotion();
  const pct = Math.round((current / total) * 100);
  const [shownPct, setShownPct] = useState(pct);
  const fromRef = useRef(pct);

  useEffect(() => {
    if (reduce) {
      fromRef.current = pct;
      setShownPct(pct);
      return;
    }
    const controls = animate(fromRef.current, pct, {
      type: "spring",
      stiffness: 120,
      damping: 20,
      onUpdate: (v) => setShownPct(Math.round(v)),
      onComplete: () => {
        fromRef.current = pct;
      },
    });
    return () => controls.stop();
  }, [pct, reduce]);

  const activePhase = PHASES.findIndex((p) => current <= p.upTo);

  return (
    <div className="border-b border-border/60 bg-background/80 backdrop-blur">
      <div className="container-page max-w-lg py-2 md:py-3">
        <div className="flex items-center justify-between gap-2 mb-1.5 md:mb-2">
          <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider">
            {PHASES.map((p, i) => (
              <span key={p.label} className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "transition-colors",
                    i === activePhase
                      ? "text-accent"
                      : i < activePhase
                      ? "text-foreground/60"
                      : "text-muted-foreground/50",
                  )}
                >
                  {p.label}
                </span>
                {i < PHASES.length - 1 && (
                  <span className="text-muted-foreground/40" aria-hidden>
                    ›
                  </span>
                )}
              </span>
            ))}
          </div>
          <span className="sm:hidden text-[11px] font-semibold uppercase tracking-wider text-accent">
            {PHASES[Math.max(0, activePhase)]?.label}
          </span>
          <span
            className="text-xs font-semibold text-muted-foreground tabular-nums"
            aria-label={`Progress ${shownPct}%`}
          >
            Step {current} of {total}
          </span>
        </div>
        <div className="relative h-1.5 rounded-full bg-muted overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-accent via-amber-400 to-accent origin-left"
            initial={false}
            animate={{ width: `${pct}%` }}
            transition={
              reduce
                ? { duration: 0 }
                : { type: "spring", stiffness: 120, damping: 20 }
            }
          />
        </div>
      </div>
    </div>
  );
}


function CountUpGbp({
  value,
  className,
  duration = 1.2,
}: {
  value: number;
  className?: string;
  duration?: number;
}) {
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(reduce ? value : 0);

  useEffect(() => {
    if (reduce) {
      setDisplay(value);
      return;
    }
    setDisplay(0);
    const controls = animate(0, value, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [value, reduce, duration]);

  return <span className={cn("tabular-nums", className)}>{gbp(display)}</span>;
}

/* -----------------------------------------------------------------------
 * Shared card shell
 * ----------------------------------------------------------------------- */
function SlideIcon({ Icon }: { Icon: React.ComponentType<{ className?: string; strokeWidth?: number; "aria-hidden"?: boolean }> }) {
  return (
    <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-accent/15 to-accent/5 border border-accent/20 shadow-sm shrink-0">
      <Icon className="h-5 w-5 text-accent" strokeWidth={2} aria-hidden />
    </span>
  );
}

function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; placeBelow: boolean } | null>(null);

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const update = () => {
      const el = btnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const width = Math.min(260, vw - 16);
      let left = rect.left + rect.width / 2;
      left = Math.max(8 + width / 2, Math.min(vw - 8 - width / 2, left));
      const placeBelow = rect.top < 120;
      const top = placeBelow ? rect.bottom + 8 : rect.top - 8;
      setPos({ top, left, placeBelow });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  return (
    <span className="relative inline-flex align-middle">
      <button
        ref={btnRef}
        type="button"
        aria-label="Why do we ask for this?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-accent/40 bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
      >
        <Info className="h-3 w-3" strokeWidth={2.5} aria-hidden />
      </button>
      <AnimatePresence>
        {open && pos && (
          <motion.span
            initial={{ opacity: 0, y: pos.placeBelow ? -4 : 4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: pos.placeBelow ? -4 : 4, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              transform: pos.placeBelow ? "translateX(-50%)" : "translate(-50%, -100%)",
              maxWidth: "calc(100vw - 16px)",
              width: 260,
            }}
            className="z-[9999] rounded-xl border border-border bg-card p-3 text-xs text-muted-foreground shadow-xl pointer-events-none"
          >
            {text}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

function Card({
  eyebrow,
  title,
  sub,
  children,
  footer,
  icon: Icon,
}: {
  eyebrow?: string;
  title: string;
  sub?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number; "aria-hidden"?: boolean }>;
}) {
  return (
    <div className="relative rounded-3xl border border-border bg-card shadow-[0_20px_60px_-20px_rgba(15,30,60,0.15)] overflow-hidden">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-transparent via-accent to-transparent"
      />
      <div className="px-4 pt-4 md:px-8 md:pt-8 pb-1.5">
        {Icon && (
          <div className="mb-1.5 md:mb-3">
            <SlideIcon Icon={Icon} />
          </div>
        )}
        {eyebrow && (
          <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-widest text-accent mb-1 md:mb-2">
            {eyebrow}
          </p>
        )}
        <h1 className="text-base md:text-2xl font-bold leading-tight text-foreground">{title}</h1>
        {sub && <p className="mt-1 md:mt-2 text-[13px] md:text-sm text-muted-foreground leading-snug">{sub}</p>}
      </div>
      <div className="px-4 md:px-8 py-3 md:py-5">{children}</div>
      {footer && (
        <div className="px-4 md:px-8 py-2.5 md:py-4 border-t border-border/60 bg-gradient-to-b from-muted/20 to-muted/40">{footer}</div>
      )}
    </div>
  );
}

function NavRow({
  onBack,
  onNext,
  nextLabel = "Continue",
  nextDisabled,
  hoverArrow,
}: {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  hoverArrow?: boolean;
}) {
  const reduce = useReducedMotion();
  const { setReg } = useContext(NextNavContext);
  // onNext is usually an inline arrow, so registering it directly would set
  // state on every render and thrash React. Keep it in a ref and register a
  // stable wrapper keyed only on the primitive props.
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  const hasNext = Boolean(onNext);
  useEffect(() => {
    if (!hasNext) {
      setReg(null);
      return;
    }
    setReg({
      onNext: () => onNextRef.current?.(),
      nextLabel,
      nextDisabled,
      hoverArrow,
    });
    return () => setReg(null);
  }, [hasNext, nextLabel, nextDisabled, hoverArrow, setReg]);

  return (
    <div className="flex items-center justify-between gap-3">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground min-h-11 px-2"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back
        </button>
      ) : (
        <span aria-hidden />
      )}
      {onNext && (
        <motion.button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          whileTap={reduce || nextDisabled ? undefined : { scale: 0.97 }}
          className="group inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground font-semibold px-5 py-2.5 text-sm shadow-cta hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed min-h-11"
        >
          {nextLabel}
          <ArrowRight
            aria-hidden
            className={cn(
              "h-4 w-4",
              hoverArrow && !reduce && "transition-transform duration-200 group-hover:translate-x-0.5",
            )}
          />
        </motion.button>
      )}
    </div>
  );
}

function OptionButtonCompact({
  active,
  onClick,
  title,
  desc,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  desc: string;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={reduce ? undefined : { scale: 0.97 }}
      className={cn(
        "relative rounded-xl border-2 px-3 py-2.5 text-left text-foreground transition-[border-color,background-color] duration-200",
        active
          ? "border-accent bg-accent/10"
          : "border-border bg-background hover:border-accent/50 hover:bg-accent/5",
      )}
    >
      <p className="text-sm font-bold leading-tight">{title}</p>
      <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{desc}</p>
    </motion.button>
  );
}

function OptionButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={reduce ? undefined : { scale: 0.97 }}
      className={cn(
        "relative w-full rounded-2xl border-2 px-4 py-4 text-left min-h-14 text-foreground transition-[border-color,background-color] duration-200",
        active
          ? "border-accent bg-accent/10"
          : "border-border bg-background hover:border-accent/50 hover:bg-accent/5",
      )}
    >
      {children}
      {active ? (
        <motion.span
          key="check"
          initial={reduce ? false : { scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={
            reduce ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 22 }
          }
          className="absolute top-3 right-3 inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground"
          aria-hidden
        >
          <Check className="h-3 w-3" strokeWidth={3} />
        </motion.span>
      ) : null}
    </motion.button>
  );
}

function NumberChips({
  value,
  onChange,
  options,
}: {
  value: number | null;
  onChange: (v: number) => void;
  options: number[];
}) {
  const reduce = useReducedMotion();
  return (
    <div className="grid grid-cols-5 gap-2">
      {options.map((n) => {
        const active = value === n;
        return (
          <motion.button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            whileTap={reduce ? undefined : { scale: 0.97 }}
            className={cn(
              "relative h-14 rounded-xl border-2 font-bold text-lg text-foreground transition-[border-color,background-color] duration-200",
              active
                ? "border-accent bg-accent/10"
                : "border-border bg-background hover:border-accent/50",
            )}
          >
            {n === 5 ? "5+" : n}
            {active ? (
              <motion.span
                key="chip-check"
                initial={reduce ? false : { scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={
                  reduce
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 420, damping: 22 }
                }
                className="absolute -top-1.5 -right-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-accent text-accent-foreground"
                aria-hidden
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </motion.span>
            ) : null}
          </motion.button>
        );
      })}
    </div>
  );
}

function NumberTiles({
  value,
  onChange,
  options,
  singular,
  plural,
  icon: Icon,
}: {
  value: number | null;
  onChange: (v: number) => void;
  options: number[];
  singular: string;
  plural: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number; "aria-hidden"?: boolean }>;
}) {
  const reduce = useReducedMotion();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5">
      {options.map((n) => {
        const active = value === n;
        const isPlus = n === options[options.length - 1] && options.length >= 5;
        const label = n === 1 && !isPlus ? singular : plural;
        return (
          <motion.button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            whileTap={reduce ? undefined : { scale: 0.97 }}
            className={cn(
              "relative flex items-center gap-3 rounded-2xl border-2 px-3 py-3 min-h-[72px] text-left transition-[border-color,background-color] duration-200",
              active
                ? "border-accent bg-accent/10"
                : "border-border bg-background hover:border-accent/50 hover:bg-accent/5",
            )}
          >
            <span
              className={cn(
                "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors",
                active
                  ? "bg-accent/15 border-accent/30 text-accent"
                  : "bg-muted/40 border-border text-muted-foreground",
              )}
              aria-hidden
            >
              <Icon className="h-5 w-5" strokeWidth={2} />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-xl font-black tabular-nums text-foreground">
                {isPlus ? `${n}+` : n}
              </span>
              <span className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
              </span>
            </span>
            {active ? (
              <motion.span
                key="tile-check"
                initial={reduce ? false : { scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={
                  reduce
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 420, damping: 22 }
                }
                className="absolute top-2 right-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground"
                aria-hidden
              >
                <Check className="h-3 w-3" strokeWidth={3} />
              </motion.span>
            ) : null}
          </motion.button>
        );
      })}
    </div>
  );
}


/* -----------------------------------------------------------------------
 * Slides
 * ----------------------------------------------------------------------- */
function SlideConfirm() {
  const reduce = useReducedMotion();
  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 12, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, y: -72, scale: 0.9 }}
      transition={
        reduce
          ? { duration: 0 }
          : { duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }
      }
      className="pointer-events-none absolute left-1/2 top-6 sm:top-8 z-30 -translate-x-1/2"
    >
      <div className="flex items-center gap-2 rounded-full bg-accent text-accent-foreground pl-2 pr-4 py-2 shadow-[0_14px_40px_-14px_rgba(234,88,12,0.65)] border border-accent/40">
        <motion.span
          initial={reduce ? false : { scale: 0.3, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={
            reduce
              ? { duration: 0 }
              : { type: "spring", stiffness: 420, damping: 18 }
          }
          className="flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-accent shadow-inner"
        >
          <Check className="h-4 w-4" strokeWidth={3.5} aria-hidden />
        </motion.span>
        <span className="text-sm font-bold tracking-tight">Got It</span>
      </div>
      <span className="sr-only">Got it, moving to next question.</span>
    </motion.div>
  );
}


function SlidePostcode({
  state,
  update,
  onNext,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onNext: () => void;
}) {
  const [touched, setTouched] = useState(false);
  const raw = state.postcode.trim();
  const valid = UK_POSTCODE.test(raw);
  const looksLikeHouseNumber = raw.length > 0 && raw.length <= 4 && /^\d+[A-Z]?$/.test(raw);
  const showErr = touched && raw.length > 0 && !valid && !looksLikeHouseNumber;

  const submit = () => {
    if (!valid) {
      setTouched(true);
      return;
    }
    const inArea = SERVICE_AREA_PREFIXES.some((p) => outwardArea(state.postcode).startsWith(p));
    update({ outOfArea: !inArea });
    onNext();
  };

  // Auto-advance shortly after the postcode is valid. A postcode that arrived
  // pre-filled from an SMS link is left alone, so the customer gets a chance to
  // see and correct it before moving on.
  useEffect(() => {
    if (!valid || state.prefilled.postcode) return;
    const t = setTimeout(() => {
      const inArea = SERVICE_AREA_PREFIXES.some((p) => outwardArea(state.postcode).startsWith(p));
      update({ outOfArea: !inArea });
      onNext();
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, state.postcode]);

  return (
    <div>
      {/* Animated sun + panel hero graphic */}
      <div className="mb-2 sm:mb-4 flex justify-center">
        <div className="relative h-14 w-14 sm:h-24 sm:w-24" aria-hidden>
          <div className="absolute inset-0 animate-[spin_18s_linear_infinite]">
            {Array.from({ length: 12 }).map((_, i) => (
              <span
                key={i}
                className="absolute left-1/2 top-1/2 block h-1 sm:h-1.5 w-0.5 rounded-full bg-accent/70"
                style={{ transform: `translate(-50%,-50%) rotate(${i * 30}deg) translateY(-26px)` }}
              />
            ))}
          </div>
          <div className="absolute inset-[26%] rounded-full bg-gradient-to-br from-amber-300 to-accent shadow-[0_0_20px_rgba(249,115,22,0.55)] animate-[pulse_2.4s_ease-in-out_infinite]" />
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 sm:w-20 h-5 sm:h-7 rounded-sm bg-primary/90 border border-primary shadow-md grid grid-cols-4 gap-[2px] p-[3px] rotate-[-8deg]">
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i} className="rounded-[1px] bg-primary-foreground/25" />
            ))}
          </div>
        </div>
      </div>

      <div className="mb-3 sm:mb-4 text-center">
        <h1 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">
          See how much solar could save your home
        </h1>
        <p className="mt-1 text-xs sm:text-sm text-muted-foreground inline-flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          About 60 seconds. No home visit, no contact details to see your figures.
        </p>
      </div>
      <Card
        title="Start with your postcode"
        sub={
          <span className="inline-flex items-center gap-2">
            We use it to price your local electricity rate.
            <InfoTooltip text="We use this to work out your local electricity price. Nothing else." />
          </span>
        }
        footer={<NavRow onNext={submit} nextDisabled={!valid} />}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="space-y-3"
        >
          <label className="block">
            <span className="text-sm font-semibold text-foreground">Postcode</span>
            {state.prefilled.postcode && (
              <PrefillChip
                onClear={() =>
                  update({ postcode: "", prefilled: { ...state.prefilled, postcode: false } })
                }
              />
            )}
            <div className="relative mt-1">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/60">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </span>
              <input
                autoFocus
                autoComplete="postal-code"
                autoCapitalize="characters"
                inputMode="text"
                spellCheck={false}
                value={state.postcode}
                onChange={(e) =>
                  update({
                    postcode: e.target.value.toUpperCase(),
                    prefilled: { ...state.prefilled, postcode: false },
                  })
                }
                onBlur={() => setTouched(true)}
                placeholder="CF11 9AA"
                aria-label="Your full postcode, for example CF11 9AA"
                className={`w-full min-h-14 rounded-2xl border-2 bg-background pl-11 pr-5 text-xl font-bold tracking-wide uppercase focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15 transition-all ${
                  state.prefilled.postcode ? PREFILLED_INPUT_CLASS : "border-input"
                }`}
              />
            </div>
          </label>

          {looksLikeHouseNumber && (
            <p className="text-sm text-muted-foreground animate-fade-in">
              That looks like a house number. Please enter your full postcode here, we'll ask for
              your house number in a moment.
            </p>
          )}
          {showErr && (
            <p className="text-sm text-destructive animate-fade-in">Please enter a valid UK postcode.</p>
          )}
          <ul className="grid gap-1 pt-1 text-[12px] text-muted-foreground">
            {[
              "Your estimated first-year saving",
              "A 25-year savings projection",
              "Recommended system size and payback",
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </form>
      </Card>
    </div>
  );
}

function SlideHouseNumber({
  state,
  update,
  onNext,
  onBack,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [touched, setTouched] = useState(false);
  const valid = state.houseNumber.trim().length > 0;

  const submit = () => {
    if (!valid) {
      setTouched(true);
      return;
    }
    onNext();
  };

  return (
    <Card
      title="House number or name?"
      sub={
        <span className="inline-flex items-center gap-2">
          So we can match your roof at {state.postcode || "your postcode"}.
          <InfoTooltip text="Used only to look up your roof size and orientation for the estimate." />
        </span>
      }
      footer={<NavRow onNext={submit} onBack={onBack} nextDisabled={!valid} />}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="block">
          <span className="sr-only">House number or name</span>
          <input
            autoComplete="address-line1"
            inputMode="text"
            maxLength={40}
            value={state.houseNumber}
            onChange={(e) => update({ houseNumber: e.target.value })}
            onBlur={() => setTouched(true)}
            placeholder="e.g. 12 or Rose Cottage"
            className="w-full min-h-14 rounded-2xl border-2 border-input bg-background px-5 text-lg font-semibold focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15 transition-all"
          />
        </label>
        {touched && !valid && (
          <p className="mt-1 text-sm text-destructive animate-fade-in">
            Please add a house number or name.
          </p>
        )}
      </form>
    </Card>
  );
}


function SlideProperty({
  state,
  update,
  onNext,
  onBack,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const reduce = useReducedMotion();
  return (
    <Card
      eyebrow="About your home"
      icon={Home}
      title="What kind of property is it?"
      sub={
        <span className="inline-flex items-center gap-2">
          Pick the closest match.
          <InfoTooltip text="Different property types use different amounts of electricity, so this helps us size your system." />
        </span>
      }
      footer={<NavRow onBack={onBack} onNext={onNext} nextDisabled={!state.property} />}
    >
      <div className="grid grid-cols-2 gap-2 sm:gap-2.5">
        {PROPERTY_OPTIONS.map((opt) => {
          const active = state.property === opt.id;
          const Icon = opt.icon;
          return (
            <motion.button
              key={opt.id}
              type="button"
              onClick={() => {
                update({ property: opt.id });
                setTimeout(() => onNext(), 200);
              }}
              whileTap={reduce ? undefined : { scale: 0.97 }}
              className={cn(
                "relative flex flex-col items-center justify-center gap-1.5 rounded-2xl border-2 px-2 py-3 min-h-[92px] text-center transition-[border-color,background-color] duration-200",
                active
                  ? "border-accent bg-accent/10"
                  : "border-border bg-background hover:border-accent/50 hover:bg-accent/5",
              )}
            >
              <span
                className={cn(
                  "inline-flex h-10 w-10 items-center justify-center rounded-xl border transition-colors",
                  active
                    ? "bg-accent/15 border-accent/30 text-accent"
                    : "bg-muted/40 border-border text-muted-foreground",
                )}
                aria-hidden
              >
                <Icon className="h-5 w-5" />
              </span>
              <p className="text-sm font-bold leading-tight text-foreground">{opt.label}</p>
              <p className="text-[11px] leading-tight text-muted-foreground">{opt.blurb}</p>
              {active ? (
                <motion.span
                  key="tile-check"
                  initial={reduce ? false : { scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 22 }}
                  className="absolute top-2 right-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-accent text-accent-foreground"
                  aria-hidden
                >
                  <Check className="h-2.5 w-2.5" strokeWidth={3} />
                </motion.span>
              ) : null}
            </motion.button>
          );
        })}
        {/* Odd-count filler to keep grid balanced on 2-col */}
        <span aria-hidden className="hidden" />
      </div>
    </Card>
  );
}

function SlideBedrooms({
  state,
  update,
  onNext,
  onBack,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <Card
      eyebrow="About your home"
      icon={Bolt}
      title="How many bedrooms?"
      sub={
        <span className="inline-flex items-center gap-2">
          Tap the number that fits.
          <InfoTooltip text="Bigger homes typically use more electricity, so this helps us estimate your annual usage." />
        </span>
      }
      footer={<NavRow onBack={onBack} onNext={onNext} nextDisabled={!state.bedrooms} />}
    >
      <NumberTiles
        value={state.bedrooms}
        onChange={(v) => {
          update({ bedrooms: v });
          setTimeout(() => onNext(), 200);
        }}
        options={[1, 2, 3, 4, 5]}
        singular="Bedroom"
        plural="Bedrooms"
        icon={Bed}
      />
    </Card>
  );
}

function SlideOccupants({
  state,
  update,
  onNext,
  onBack,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <Card
      eyebrow="About your home"
      icon={Users}
      title="How many people live there?"
      sub={
        <span className="inline-flex items-center gap-2">
          Count everyone in the house.
          <InfoTooltip text="Each extra person adds around 500 kWh a year to your bill, so this sharpens your estimate." />
        </span>
      }
      footer={<NavRow onBack={onBack} onNext={onNext} nextDisabled={!state.occupants} />}
    >
      <NumberTiles
        value={state.occupants}
        onChange={(v) => {
          update({ occupants: v });
          setTimeout(() => onNext(), 200);
        }}
        options={[1, 2, 3, 4, 5]}
        singular="Person"
        plural="People"
        icon={Users}
      />
    </Card>
  );
}

function SlideUsage({
  state,
  update,
  onNext,
  onBack,
  sessionId,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onNext: () => void;
  onBack: () => void;
  sessionId: string;
}) {
  const rate = rateForPostcode(state.postcode);

  const canContinue =
    state.useEstimate ||
    (state.overrideKwh !== null && state.overrideKwh > 0) ||
    (state.overrideAnnualCost !== null && state.overrideAnnualCost > 0);

  const uploadBills = async (files: File[]) => {
    if (files.length === 0) return;
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId || "unknown");
      fd.append("first_name", state.firstName);
      fd.append("phone", state.phone);
      fd.append("email", state.email);
      fd.append("postcode", state.postcode);
      fd.append("house_number", state.houseNumber);
      if (typeof window !== "undefined") fd.append("page_url", window.location.href);
      for (const [k, v] of Object.entries(trafficFields())) {
        fd.append(k, v ?? "");
      }
      files.forEach((f) => fd.append("files", f, f.name));

      await fetch("/api/public/solar-calculator-bills-webhook", {
        method: "POST",
        body: fd,
      });
    } catch (err) {
      console.error("[solar-savings-calculator] bill upload failed", err);
    }
  };

  if (state.knowsUsage !== true) {
    return (
      <Card
        eyebrow="Your usage"
        icon={Zap}
        title="Do you know your energy usage?"
        sub={
          <span className="inline-flex items-center gap-2">
            If not, that's fine, we'll estimate it from your home.
            <InfoTooltip text="Your annual kWh usage sets the size of the savings. A bill is most accurate, but a home-based estimate works too." />
          </span>
        }
        footer={<NavRow onBack={onBack} onNext={onNext} nextDisabled />}
      >
        <div className="grid grid-cols-2 gap-2">
          <OptionButtonCompact
            active={false}
            onClick={() => update({ knowsUsage: true })}
            title="Yes"
            desc="I'll enter it or upload a bill"
          />
          <OptionButtonCompact
            active={false}
            onClick={() => {
              update({
                knowsUsage: false,
                useEstimate: true,
                overrideKwh: null,
                overrideAnnualCost: null,
              });
              onNext();
            }}
            title="No"
            desc="Estimate it for me"
          />
        </div>
      </Card>
    );
  }

  return (
    <Card
      eyebrow="Your usage"
      icon={Zap}
      title="Estimate your usage"
      sub={
        <span className="inline-flex items-center gap-2">
          Upload a bill, or estimate it from your home.
          <InfoTooltip text="Your annual kWh usage sets the size of the savings. A bill is most accurate, but a home-based estimate works too." />
        </span>
      }
      footer={
        <NavRow
          onBack={() => update({ knowsUsage: null })}
          onNext={onNext}
          nextDisabled={!canContinue}
        />
      }
    >
      <div className="space-y-2">
        <BillScanner
          onScanned={(files) => {
            update({
              useEstimate: false,
              overrideKwh: null,
              overrideAnnualCost: null,
            });
            void uploadBills(files);
          }}
        />
        <div className="grid grid-cols-2 gap-2">
          <OptionButtonCompact
            active={state.useEstimate}
            onClick={() =>
              update({ useEstimate: true, overrideKwh: null, overrideAnnualCost: null })
            }
            title="Estimate for me"
            desc="From your home"
          />
          <OptionButtonCompact
            active={!state.useEstimate}
            onClick={() => update({ useEstimate: false })}
            title="I know my figure"
            desc="Enter it below"
          />
        </div>

        {!state.useEstimate && (
          <div className="mt-4 space-y-3 rounded-2xl border border-border bg-muted/40 p-4">
            <label className="block">
              <span className="text-sm font-semibold">Annual usage (kWh)</span>
              <input
                type="number"
                inputMode="numeric"
                min={500}
                max={30000}
                value={state.overrideKwh ?? ""}
                onChange={(e) =>
                  update({
                    overrideKwh: e.target.value ? Number(e.target.value) : null,
                    overrideAnnualCost: null,
                  })
                }
                placeholder="e.g. 3500"
                className="mt-1 block w-full min-h-12 rounded-xl border border-input bg-background px-4 text-base focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
            <p className="text-center text-xs uppercase tracking-wider text-muted-foreground">or</p>
            <label className="block">
              <span className="text-sm font-semibold">Annual electricity cost (£)</span>
              <input
                type="number"
                inputMode="numeric"
                min={100}
                max={10000}
                value={state.overrideAnnualCost ?? ""}
                onChange={(e) =>
                  update({
                    overrideAnnualCost: e.target.value ? Number(e.target.value) : null,
                    overrideKwh: null,
                  })
                }
                placeholder="e.g. 1200"
                className="mt-1 block w-full min-h-12 rounded-xl border border-input bg-background px-4 text-base focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
            <p className="text-xs text-muted-foreground">
              We convert £ to kWh at your local rate ({(rate * 100).toFixed(2)}p/kWh).
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

function SlidePanels({
  state,
  update,
  onNext,
  onBack,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (state.panels !== null) return;
    if (!state.property) return;
    update({ panels: DEFAULT_PANELS_BY_PROPERTY[state.property] });
  }, [state.panels, state.property, update]);

  const panels = state.panels;
  const suggested =
    state.property != null ? DEFAULT_PANELS_BY_PROPERTY[state.property] : MIN_PANELS;
  const suggestedKwp = kwpForPanels(suggested);



  const setPanels = (n: number) => {
    update({ panels: Math.min(MAX_PANELS, Math.max(MIN_PANELS, n)) });
  };

  const knows = state.knowsPanels;

  if (knows !== true) {
    return (
      <Card
        eyebrow="Build your system"
        icon={Sun}
        title="Do you know how many solar panels you'd like?"
        sub={
          <span className="inline-flex items-center gap-2">
            If not, that's fine, we'll suggest a system for your home.
            <InfoTooltip text={`Based on your home we'd suggest ${suggested} panels (${suggestedKwp} kWp). We confirm the exact system after a roof check.`} />
          </span>
        }
        footer={<NavRow onBack={onBack} onNext={onNext} nextDisabled />}
      >
        <div className="grid grid-cols-2 gap-2">
          <OptionButtonCompact
            active={false}
            onClick={() => {
              update({ knowsPanels: true, panels: panels ?? suggested });
            }}
            title="Yes"
            desc="I'll choose the number"
          />
          <OptionButtonCompact
            active={false}
            onClick={() => {
              update({ knowsPanels: false, panels: suggested });
              onNext();
            }}
            title="No"
            desc="Suggest one for me"
          />
        </div>
      </Card>
    );
  }

  return (
    <Card
      eyebrow="Build your system"
      icon={Sun}
      title="How many solar panels?"
      sub={
        <span className="inline-flex items-center gap-2">
          We'd suggest {suggested} panels. Adjust if you'd like more.
          <InfoTooltip text={`Based on your home we'd suggest ${suggested} panels (${suggestedKwp} kWp). You can add more if you have the roof space. We'll confirm the exact system for your roof when we speak.`} />
        </span>
      }
      footer={
        <NavRow
          onBack={() => update({ knowsPanels: null })}
          onNext={onNext}
          nextDisabled={panels === null}
        />
      }
    >
      <div className="space-y-5">
        <div className="flex items-center justify-center gap-4">

          <motion.button
            type="button"
            aria-label="Fewer panels"
            disabled={panels === null || panels <= MIN_PANELS}
            onClick={() => panels != null && setPanels(panels - 1)}
            whileTap={reduce ? undefined : { scale: 0.9 }}
            className="h-14 w-14 rounded-full border border-border bg-card text-2xl font-bold text-foreground hover:bg-muted disabled:opacity-40 disabled:pointer-events-none"
          >
            −
          </motion.button>
          <div className="min-w-[7rem] text-center overflow-hidden" style={{ perspective: 480 }}>
            <motion.p
              key={panels ?? "dash"}
              initial={reduce ? false : { rotateX: -75, opacity: 0 }}
              animate={{ rotateX: 0, opacity: 1 }}
              transition={{ duration: reduce ? 0 : 0.22, ease: "easeOut" }}
              className="text-5xl font-black tabular-nums text-primary leading-none"
              style={{ transformOrigin: "center" }}
            >
              {panels ?? "-"}
            </motion.p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Panels
            </p>
          </div>
          <motion.button
            type="button"
            aria-label="More panels"
            disabled={panels === null || panels >= MAX_PANELS}
            onClick={() => panels != null && setPanels(panels + 1)}
            whileTap={reduce ? undefined : { scale: 0.9 }}
            className="h-14 w-14 rounded-full border border-border bg-card text-2xl font-bold text-foreground hover:bg-muted disabled:opacity-40 disabled:pointer-events-none"
          >
            +
          </motion.button>
        </div>

        {panels != null && panels > 0 ? (
          <div className="flex flex-wrap justify-center gap-1.5 px-2" aria-hidden>
            {Array.from({ length: panels }, (_, i) => (
              <motion.span
                key={i}
                initial={reduce ? false : { scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={
                  reduce
                    ? { duration: 0 }
                    : {
                        type: "spring",
                        stiffness: 420,
                        damping: 22,
                        delay: Math.min(i, 40) * 0.02,
                      }
                }
                className="h-3 w-5 rounded-sm bg-accent/80"
              />
            ))}
          </div>
        ) : null}

      </div>
    </Card>
  );
}

function SlideAddons({
  state,
  update,
  onNext,
  onBack,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const count = state.batteryCount;
  const setCount = (n: number) => {
    const next = Math.max(0, Math.min(3, n));
    if (next > 0 && !state.hasBattery) trackBatteryAdded();
    update({ batteryCount: next, hasBattery: next > 0 });
  };
  return (
    <Card
      eyebrow="Build Your System"
      icon={Battery}
      title="Battery Storage"
      sub={
        <span className="inline-flex items-center gap-2">
          Choose how many battery units to include.
          <InfoTooltip text="A battery stores daytime solar for the evening so you use around 75% of your solar at home instead of 40%." />
        </span>
      }
      footer={<NavRow onBack={onBack} onNext={onNext} />}
    >
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex items-start gap-3">
          <Battery className="h-6 w-6 text-accent shrink-0 mt-0.5" aria-hidden />
          <div className="flex-1">
            <p className="font-bold text-foreground">{BATTERY_OPTION.label}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {BATTERY_OPTION.model} · {BATTERY_OPTION.usableKwh} kWh usable per unit.
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-muted/60 p-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              Quantity
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {count === 0
                ? "No battery, panels only."
                : `${(count * BATTERY_OPTION.usableKwh).toFixed(1)} kWh usable total`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Decrease battery quantity"
              onClick={() => setCount(count - 1)}
              disabled={count <= 0}
              className="h-10 w-10 rounded-full border border-input bg-background text-lg font-bold text-foreground disabled:opacity-40 hover:bg-muted transition-colors"
            >
              −
            </button>
            <span className="min-w-[2rem] text-center text-2xl font-black text-foreground tabular-nums">
              {count}
            </span>
            <button
              type="button"
              aria-label="Increase battery quantity"
              onClick={() => setCount(count + 1)}
              disabled={count >= 3}
              className="h-10 w-10 rounded-full border border-accent bg-accent text-lg font-bold text-accent-foreground disabled:opacity-40 hover:bg-accent/90 transition-colors"
            >
              +
            </button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Not sure? One battery suits most homes. You can change this later when we speak.
        </p>
      </div>
    </Card>
  );
}


function SlideContact({
  state,
  update,
  onNext,
  onBack,
}: {
  state: WizardState;
  update: (p: Partial<WizardState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const nameOk = state.firstName.trim().length > 0;
  const phoneOk = isValidUkPhone(state.phone);
  const emailOk = state.email === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email);
  // Marketing consent is optional: they can see their figures either way.
  const canContinue = nameOk && phoneOk && emailOk;
  const advancedRef = useRef(false);
  useEffect(() => {
    if (!canContinue || advancedRef.current) return;
    const t = setTimeout(() => {
      if (!advancedRef.current) {
        advancedRef.current = true;
        onNext();
      }
    }, 650);
    return () => clearTimeout(t);
  }, [canContinue, onNext]);
  const inputCls =
    "mt-1 block w-full min-h-12 rounded-xl border border-input bg-background px-4 text-base focus:outline-none focus:ring-2 focus:ring-accent transition-[box-shadow] duration-200";

  return (
    <Card
      eyebrow="Almost there"
      icon={Mail}
      title="Where should we send your projection?"
      sub={
        <span className="inline-flex items-center gap-2">
          Add your details for the full 25-year breakdown.
          <InfoTooltip text="We'll show your figures on the next screen and email a copy. We only follow up if you'd like a real quote." />
        </span>
      }
      footer={
        <NavRow
          onBack={onBack}
          onNext={onNext}
          nextDisabled={!canContinue}
          nextLabel="See my projection"
          hoverArrow
        />
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canContinue) onNext();
        }}
        className="space-y-3"
      >
        <label className="block">
          <span className="text-sm font-semibold">First name</span>
          {state.prefilled.firstName && (
            <PrefillChip
              onClear={() =>
                update({ firstName: "", prefilled: { ...state.prefilled, firstName: false } })
              }
            />
          )}
          <input
            autoComplete="given-name"
            value={state.firstName}
            onChange={(e) =>
              update({
                firstName: e.target.value,
                prefilled: { ...state.prefilled, firstName: false },
              })
            }
            className={`${inputCls} ${state.prefilled.firstName ? PREFILLED_INPUT_CLASS : ""}`}
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Phone number</span>
          {state.prefilled.phone && (
            <PrefillChip
              onClear={() =>
                update({ phone: "", prefilled: { ...state.prefilled, phone: false } })
              }
            />
          )}
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="07..."
            value={state.phone}
            onChange={(e) =>
              update({ phone: e.target.value, prefilled: { ...state.prefilled, phone: false } })
            }
            className={`${inputCls} ${state.prefilled.phone ? PREFILLED_INPUT_CLASS : ""}`}
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold">
            Email <span className="font-normal text-muted-foreground">(optional)</span>
          </span>
          {state.prefilled.email && (
            <PrefillChip
              onClear={() =>
                update({ email: "", prefilled: { ...state.prefilled, email: false } })
              }
            />
          )}
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={state.email}
            onChange={(e) =>
              update({ email: e.target.value, prefilled: { ...state.prefilled, email: false } })
            }
            className={`${inputCls} ${state.prefilled.email ? PREFILLED_INPUT_CLASS : ""}`}
          />
        </label>

        <label className="flex items-start gap-3 text-[13px] text-muted-foreground pt-1">
          <input
            type="checkbox"
            checked={state.consent}
            onChange={(e) => update({ consent: e.target.checked })}
            className="mt-1 h-5 w-5 accent-[var(--accent)]"
          />
          <span>
            <span className="font-semibold text-foreground">Optional:</span> I'm happy for CRS
            Energy to contact me about my solar estimate. You can opt out any time. See our{" "}
            <a href="/privacy" className="underline">Privacy Policy</a>.
          </span>
        </label>
      </form>
      <p className="mt-3 text-[11px] text-center text-muted-foreground inline-flex items-center gap-1.5 w-full justify-center">
        <ShieldCheck className="h-3 w-3 text-emerald-600" aria-hidden />
        We don't share your details. No spam calls.
      </p>
    </Card>
  );
}

/* Chart lazy-loaded */
const LazyChart = lazy(() => import("@/components/solar-calc-chart"));

function SlideGraphGridOnly({
  projection,
  onNext,
  onBack,
}: {
  projection: ReturnType<typeof buildProjection>;
  onNext: () => void;
  onBack: () => void;
}) {
  const firstYear = projection.series[0].gridOnly;
  const lastYear = projection.series[projection.series.length - 1].gridOnly;
  // Percentage growth of cumulative bill over 25 years (illustrative).
  const growthPct = firstYear > 0 ? Math.round(((lastYear / firstYear) - 1) * 100) : 0;
  return (
    <Card
      eyebrow="Step 1 of 2"
      title="Sticking with the grid"
      sub="If you carry on paying your supplier, here's how the cost keeps climbing over 25 years. We assume energy prices rise 5% a year."
      footer={<NavRow onBack={onBack} onNext={onNext} nextLabel="Now add solar" />}
    >
      <div className="space-y-4">
        <div className="rounded-2xl bg-destructive/10 border border-destructive/20 p-4 text-center">
          <p className="text-[11px] uppercase tracking-widest text-destructive/80 font-bold">
            25-Year Bill Growth
          </p>
          <p className="text-4xl md:text-5xl font-black text-destructive leading-none mt-1">
            +{growthPct}%
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            That's roughly how much more your yearly bill could be by year 25 if nothing changes.
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-2">
          <Suspense
            fallback={
              <div className="h-64 flex items-center justify-center text-xs text-muted-foreground">
                Loading chart...
              </div>
            }
          >
            <LazyChart series={projection.series} mode="grid-only" hideValues />
          </Suspense>
        </div>
        <p className="text-xs text-muted-foreground text-center">
          Your exact costs will be confirmed once we've completed your bespoke design.
        </p>
      </div>
    </Card>
  );
}


function SlideGraphCompare({
  projection,
  hasBattery,
  onNext,
  onBack,
}: {
  projection: ReturnType<typeof buildProjection>;
  hasBattery: boolean;
  onNext: () => void;
  onBack: () => void;
}) {
  const reduce = useReducedMotion();
  // 0 = counting, 1 = glow, 2 = chart visible
  const [phase, setPhase] = useState(reduce ? 2 : 0);

  useEffect(() => {
    if (reduce) {
      setPhase(2);
      return;
    }
    setPhase(0);
    const glowAt = window.setTimeout(() => setPhase(1), 1200);
    const chartAt = window.setTimeout(() => setPhase(2), 1800);
    return () => {
      window.clearTimeout(glowAt);
      window.clearTimeout(chartAt);
    };
  }, [reduce]);

  const grid25 = projection.series[projection.series.length - 1].gridOnly;
  const savingsPct = grid25 > 0 ? Math.round((projection.lifetimeSaving / grid25) * 100) : 0;
  return (
    <Card
      eyebrow="Step 2 of 2"
      title="With CRS solar added"
      sub="The green line is the same 25 years with a CRS solar system fitted. The shaded area is what you save."
      footer={<NavRow onBack={onBack} onNext={onNext} nextLabel="See the summary" />}
    >
      <div className="space-y-4">
        <div className="relative rounded-2xl bg-success/10 border border-success/20 p-4 text-center overflow-hidden">
          {phase >= 1 && !reduce ? (
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-2xl border-2 border-success"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            />
          ) : null}
          <p className="text-[11px] uppercase tracking-widest text-success font-bold">
            Estimated 25-Year Saving
          </p>
          <p className="text-4xl md:text-5xl font-black text-success leading-none mt-1">
            ~{savingsPct}%
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {hasBattery
              ? "Includes a battery so around 75% of your solar is used at home."
              : "Panels only, around 40% self-used and the rest exported."}
          </p>
        </div>
        {phase >= 2 ? (
          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: reduce ? 0 : 0.35 }}
            className="rounded-2xl border border-border bg-card p-2"
          >
            <Suspense
              fallback={
                <div className="h-64 flex items-center justify-center text-xs text-muted-foreground">
                  Loading chart...
                </div>
              }
            >
              <LazyChart
                series={projection.series}
                mode="compare"
                paybackYear={projection.paybackYears}
                hideValues
              />
            </Suspense>
          </motion.div>
        ) : (
          <div className="h-64 rounded-2xl border border-border bg-card" aria-hidden />
        )}
        <p className="text-xs text-muted-foreground text-center">
          Illustration only. Your exact figures will be confirmed once we've completed your bespoke design.
        </p>
      </div>
    </Card>
  );
}


function SlideResults({
  projection,
  firstName,
  state,
  onBack,
}: {
  projection: ReturnType<typeof buildProjection>;
  firstName: string;
  state: WizardState;
  onBack: () => void;
}) {
  const reduce = useReducedMotion();
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 40;
      const contentW = pageW - marginX * 2;

      // Brand palette (RGB)
      const NAVY: [number, number, number] = [15, 30, 60];
      const NAVY_SOFT: [number, number, number] = [30, 50, 90];
      const ORANGE: [number, number, number] = [234, 88, 12];
      const ORANGE_SOFT: [number, number, number] = [255, 237, 213];
      const INK: [number, number, number] = [17, 24, 39];
      const MUTED: [number, number, number] = [107, 114, 128];
      const HAIR: [number, number, number] = [229, 231, 235];
      const CARD: [number, number, number] = [249, 250, 251];

      const setFill = (c: [number, number, number]) => doc.setFillColor(c[0], c[1], c[2]);
      const setText = (c: [number, number, number]) => doc.setTextColor(c[0], c[1], c[2]);
      const setDraw = (c: [number, number, number]) => doc.setDrawColor(c[0], c[1], c[2]);

      /* ---------- Header band ---------- */
      setFill(NAVY);
      doc.rect(0, 0, pageW, 96, "F");
      setFill(ORANGE);
      doc.rect(0, 96, pageW, 4, "F");

      setText([255, 255, 255]);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("CRS Energy", marginX, 42);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      setText([203, 213, 225]);
      doc.text("Your Personalised Solar Estimate", marginX, 60);
      doc.setFontSize(9);
      doc.text(
        `Prepared ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}${firstName ? ` for ${firstName}` : ""}`,
        marginX,
        76,
      );

      // Top-right contact block
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      setText([255, 255, 255]);
      doc.text("01656 339844", pageW - marginX, 42, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      setText([203, 213, 225]);
      doc.text("crsenergy.org", pageW - marginX, 58, { align: "right" });
      doc.text("hello@crsenergy.org", pageW - marginX, 72, { align: "right" });

      let y = 128;

      /* ---------- Headline: we'll be in touch ---------- */
      setFill(ORANGE_SOFT);
      doc.roundedRect(marginX, y, contentW, 88, 10, 10, "F");
      setDraw(ORANGE);
      doc.setLineWidth(0.6);
      doc.roundedRect(marginX, y, contentW, 88, 10, 10, "S");

      setText(NAVY);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Thanks - We'll Be In Touch Within 24-48 Hours", pageW / 2, y + 34, {
        align: "center",
      });
      setText(MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const hb = doc.splitTextToSize(
        "A member of our team will confirm your exact roof, price up the system and walk you through the finance options that fit. Prefer to book a slot now? Head to crsenergy.org/book.",
        contentW - 40,
      ) as string[];
      doc.text(hb, pageW / 2, y + 54, { align: "center" });
      y += 88 + 20;

      /* ---------- Section: Your details ---------- */
      const sectionTitle = (label: string) => {
        setText(ORANGE);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(label.toUpperCase(), marginX, y);
        y += 6;
        setDraw(ORANGE);
        doc.setLineWidth(1.5);
        doc.line(marginX, y, marginX + 30, y);
        y += 14;
      };

      sectionTitle("What You Told Us");

      // Two-column detail grid (no figures / no pricing)
      const details: Array<[string, string]> = [
        ["Name", firstName || "-"],
        ["Address", `${state.houseNumber || "-"}, ${state.postcode || "-"}`],
        ["Property", `${state.property || "-"}, ${state.bedrooms || "-"} bed`],
        ["Occupants", String(state.occupants || "-")],
        ["Panels Chosen", String(projection.panels)],
        ["Battery", state.batteryCount > 0 ? `${state.batteryCount} × ${BATTERY_OPTION.usableKwh} kWh` : "Not Included"],
      ];

      const rowH = 22;
      const rows = Math.ceil(details.length / 2);
      const gridH = rows * rowH + 12;
      setFill(CARD);
      doc.roundedRect(marginX, y, contentW, gridH, 8, 8, "F");
      setDraw(HAIR);
      doc.setLineWidth(0.4);
      doc.roundedRect(marginX, y, contentW, gridH, 8, 8, "S");

      const cellW = contentW / 2;
      details.forEach((d, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const cx = marginX + 14 + col * cellW;
        const cy = y + 18 + row * rowH;
        setText(MUTED);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(d[0].toUpperCase(), cx, cy);
        setText(INK);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(d[1], cx, cy + 12);
      });
      y += gridH + 22;


      /* ---------- Section: How it works ---------- */
      sectionTitle("How It Works, Step By Step");

      const steps: Array<[string, string]> = [
        ["Get In Touch", "Send a short enquiry or book a call - just your postcode and what you're interested in."],
        ["Your Solar Call", "We talk through your property, how you use electricity and what you want from solar."],
        ["Your Estimate", "You'll get an indicative system recommendation and estimated price, followed up in writing."],
        ["Choose How To Pay", "Pay in full, or apply for finance through our partner Ideal4Finance - always your choice."],
        ["Desktop Survey & Design", "We complete your full system design remotely using aerial mapping and roof analysis."],
        ["Installation", "Your MCS-certified installation is scheduled and completed by our accredited team."],
      ];

      steps.forEach((s, i) => {
        // number pill
        setFill(NAVY);
        doc.circle(marginX + 10, y + 4, 9, "F");
        setText([255, 255, 255]);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(String(i + 1), marginX + 10, y + 7, { align: "center" });

        setText(INK);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(s[0], marginX + 28, y + 4);

        setText(MUTED);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        const bodyLines = doc.splitTextToSize(s[1], contentW - 32) as string[];
        doc.text(bodyLines, marginX + 28, y + 18);
        y += 18 + bodyLines.length * 11 + 6;
      });

      /* ---------- Page 2: Why choose CRS + Accreditations + CTA ---------- */
      doc.addPage();
      y = 48;

      sectionTitle("Why Homeowners Choose CRS Energy");

      const whys: Array<[string, string]> = [
        [
          "Generate Your Own Electricity",
          "Your panels turn daylight into electricity all year round, with an MCS performance estimate for your roof.",
        ],
        [
          "Use What You Generate",
          "A home battery stores unused daytime electricity for the evening, when your home needs it most.",
        ],
        [
          "Get Paid For Excess Energy",
          "Sell surplus back to the grid via the Smart Export Guarantee. We handle the SEG registration for you.",
        ],
        [
          "Designed Around Your Home",
          "No off-the-shelf packages. Sized to your roof, your usage and how you live. Plain-English, no pressure.",
        ],
      ];

      const cardH = 62;
      const cardGap = 10;
      const cardW = (contentW - cardGap) / 2;
      whys.forEach((w, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const cx = marginX + col * (cardW + cardGap);
        const cy = y + row * (cardH + cardGap);
        setFill(CARD);
        doc.roundedRect(cx, cy, cardW, cardH, 8, 8, "F");
        setDraw(HAIR);
        doc.setLineWidth(0.4);
        doc.roundedRect(cx, cy, cardW, cardH, 8, 8, "S");
        // orange accent bar
        setFill(ORANGE);
        doc.roundedRect(cx, cy, 3, cardH, 1.5, 1.5, "F");

        setText(NAVY);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10.5);
        doc.text(w[0], cx + 12, cy + 16);
        setText(MUTED);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const wl = doc.splitTextToSize(w[1], cardW - 20) as string[];
        doc.text(wl, cx + 12, cy + 30);
      });
      y += Math.ceil(whys.length / 2) * (cardH + cardGap) + 14;

      /* Accreditations */
      sectionTitle("Accredited & Certified");
      setFill(CARD);
      doc.roundedRect(marginX, y, contentW, 62, 8, 8, "F");
      setDraw(HAIR);
      doc.setLineWidth(0.4);
      doc.roundedRect(marginX, y, contentW, 62, 8, 8, "S");
      setText(INK);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("MCS Certified Installer  |  HIES Consumer Code  |  TrustMark Registered", marginX + 14, y + 22);
      setText(MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const accText = doc.splitTextToSize(
        "Every install is MCS certified, DNO compliant and manufacturer-backed. Members of the HIES Consumer Code and TrustMark registered - your deposit and workmanship are protected.",
        contentW - 28,
      ) as string[];
      doc.text(accText, marginX + 14, y + 38);
      y += 62 + 20;

      /* ---------- CTA card ---------- */
      setFill(NAVY);
      doc.roundedRect(marginX, y, contentW, 130, 10, 10, "F");
      setFill(ORANGE);
      doc.roundedRect(marginX, y, 4, 130, 2, 2, "F");

      setText([255, 255, 255]);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Ready For A Firm Quote?", marginX + 20, y + 28);
      setText([203, 213, 225]);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const ctaBody = doc.splitTextToSize(
        "Book a free 15-minute call. We'll confirm your exact roof, price up the system and walk you through the finance options that fit.",
        contentW - 40,
      ) as string[];
      doc.text(ctaBody, marginX + 20, y + 48);

      setText([255, 255, 255]);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Call:  01656 339844", marginX + 20, y + 92);
      doc.text("Web:  crsenergy.org", marginX + 20, y + 110);
      doc.text("Email:  hello@crsenergy.org", marginX + 200, y + 92);
      doc.text("Book:  crsenergy.org/book", marginX + 200, y + 110);

      /* ---------- Footer disclaimer ---------- */
      setText(MUTED);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      const disc = doc.splitTextToSize(
        "Illustration only, not a firm quote. Assumes 5% energy inflation and 0.5% panel degradation per year. Final figures confirmed on a free call after a roof check. Complete Remedial Solutions (CRS) Ltd. Finance subject to status.",
        contentW,
      ) as string[];
      doc.text(disc, marginX, pageH - 34);

      doc.save(`crs-solar-estimate${firstName ? `-${firstName.toLowerCase()}` : ""}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card
      eyebrow="Your summary"
      title={firstName ? `${firstName}, let's build your finance-friendly plan` : "Let's build your finance-friendly plan"}
      sub="One call. We'll confirm your exact roof, price up the system and walk you through the finance options that fit."
      footer={
        <div className="space-y-3">
          <motion.a
            href={BOOKING_URL}
            onClick={() => trackScheduleClick()}
            className="flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground font-bold px-6 py-3.5 shadow-cta hover:bg-accent/90 min-h-12"
            animate={
              reduce
                ? undefined
                : {
                    boxShadow: [
                      "0 10px 25px -5px rgba(234, 88, 12, 0.35)",
                      "0 14px 32px -4px rgba(234, 88, 12, 0.55)",
                      "0 10px 25px -5px rgba(234, 88, 12, 0.35)",
                    ],
                  }
            }
            transition={
              reduce
                ? undefined
                : { duration: 2.5, repeat: Infinity, ease: "easeInOut" }
            }
          >
            Book Your Free Call
            <ArrowRight className="h-4 w-4" aria-hidden />
          </motion.a>
          <div className="grid grid-cols-2 gap-2">
            <a
              href="/solar-pv"
              className="flex items-center justify-center gap-1.5 rounded-full border-2 border-border px-4 py-3 font-semibold text-sm hover:bg-muted min-h-12 text-center"
            >
              Find Out More
            </a>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center justify-center gap-1.5 rounded-full border-2 border-border px-4 py-3 font-semibold text-sm hover:bg-muted min-h-12 disabled:opacity-60"
            >
              {downloading ? "Preparing..." : "Download Estimate PDF"}
            </button>
          </div>
          <a
            href={`tel:${PHONE_NUMBER.replace(/\s+/g, "")}`}
            onClick={() => trackContactClick()}
            className="flex items-center justify-center gap-2 rounded-full border-2 border-border px-6 py-3 font-semibold text-sm hover:bg-muted min-h-12"
          >
            <Phone className="h-4 w-4" aria-hidden />
            Or Call {PHONE_NUMBER}
          </a>
          <button
            type="button"
            onClick={onBack}
            className="w-full text-xs text-muted-foreground hover:text-foreground"
          >
            Back
          </button>
        </div>
      }
    >
      <div className="rounded-2xl bg-success/10 border border-success/20 p-4 text-center">
        <p className="text-[11px] uppercase tracking-widest text-success font-bold">
          What Happens Next
        </p>
        <p className="mt-1 text-lg md:text-xl font-black text-foreground leading-tight">
          We'll be in touch within 24-48 hours
        </p>
        <p className="mt-1.5 text-xs text-muted-foreground">
          A member of our team will confirm your roof, price up the system and walk you through your finance options. Prefer to book a slot? Use the button below.
        </p>
      </div>
      <div className="mt-3 rounded-2xl border border-border bg-muted/40 p-4 text-xs text-muted-foreground space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider font-bold text-foreground/70">
          What You Told Us
        </p>
        <p>
          <strong className="text-foreground">Address:</strong>{" "}
          {state.houseNumber ? `${state.houseNumber}, ` : ""}{state.postcode || "-"}
        </p>
        <p>
          <strong className="text-foreground">Property:</strong>{" "}
          {state.property || "-"}, {state.bedrooms || "-"} bed, {state.occupants || "-"} occupants
        </p>
        <p>
          <strong className="text-foreground">Panels chosen:</strong> {projection.panels}
        </p>
        <p>
          <strong className="text-foreground">Battery:</strong>{" "}
          {state.batteryCount > 0
            ? `${state.batteryCount} × ${BATTERY_OPTION.usableKwh} kWh`
            : "Not included"}
        </p>
      </div>

      {/* Mini timeline */}
      <div className="mt-4">
        <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2.5">
          What Happens Next
        </p>
        <ol className="space-y-2.5">
          {[
            { Icon: Clock, title: "Call within 24-48h", body: "A quick chat to confirm your roof and answer questions." },
            { Icon: FileCheck, title: "Free bespoke design", body: "We size the system around your usage and finance options." },
            { Icon: Award, title: "Written quote", body: "Plain-English, no pressure. Yours to compare." },
          ].map(({ Icon, title, body }, i) => (
            <li key={title} className="flex items-start gap-3">
              <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/10 border border-accent/20">
                <Icon className="h-4 w-4 text-accent" aria-hidden />
                <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                  {i + 1}
                </span>
              </span>
              <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-xs font-bold text-foreground leading-tight">{title}</p>
                <p className="text-[11px] text-muted-foreground leading-snug">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Guarantee row */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          { Icon: ShieldCheck, label: "MCS Certified" },
          { Icon: Award, label: "6-Yr Warranty" },
          { Icon: Sparkles, label: "0% VAT" },
        ].map(({ Icon, label }) => (
          <div
            key={label}
            className="flex flex-col items-center gap-1 rounded-xl border border-border/60 bg-muted/30 py-2.5 px-1 text-center"
          >
            <Icon className="h-4 w-4 text-accent" aria-hidden />
            <span className="text-[10px] font-bold text-foreground leading-tight">{label}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function StatTile({
  label,
  value,
  sub,
  tone,
  index = 0,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  tone: "success" | "primary" | "muted";
  index?: number;
}) {
  const reduce = useReducedMotion();
  const toneCls =
    tone === "success"
      ? "bg-success/10 border-success/20 text-success"
      : tone === "primary"
      ? "bg-primary/10 border-primary/20 text-primary"
      : "bg-card border-border text-foreground";
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reduce ? 0 : 0.28,
        delay: reduce ? 0 : index * 0.08,
        ease: "easeOut",
      }}
      className={`rounded-xl border p-3 text-center ${toneCls}`}
    >
      <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold">{label}</p>
      <p className="mt-1 text-lg md:text-xl font-black tabular-nums leading-none">{value}</p>
      {sub && <p className="mt-0.5 text-[10px] opacity-70">{sub}</p>}
    </motion.div>
  );
}

/* -----------------------------------------------------------------------
 * Webhook
 * ----------------------------------------------------------------------- */
async function sendWebhook(s: WizardState): Promise<void> {
  const kwh = derivedAnnualKwh(s);
  if (!s.panels || !kwh) return;
  const rate = rateForPostcode(s.postcode);
  const projection = buildProjection({
    annualUsageKwh: kwh,
    unitRate: rate,
    panels: s.panels,
    hasBattery: s.hasBattery,
    addonIds: s.addons,
    batteryCount: s.batteryCount,
  });
  const payload = {
    firstName: s.firstName,
    phone: s.phone,
    email: s.email,
    postcode: s.postcode,
    houseNumber: s.houseNumber.trim(),
    region: regionForPostcode(s.postcode),
    outOfArea: s.outOfArea,
    propertyType: s.property,
    bedrooms: s.bedrooms,
    occupants: s.occupants,
    annualUsageKwh: projection.annualUsageKwh,
    unitRateUsed: rate,
    panelCount: s.panels,
    systemKwp: projection.systemKwp,
    pricingKwp: projection.pricingKwp,
    hasBattery: s.hasBattery,
    batteryCount: s.batteryCount,
    addons: s.addons,
    systemPrice: projection.systemPrice,
    year1Saving: projection.annualSavingYear1,
    lifetimeSaving: projection.lifetimeSaving,
    paybackYears: projection.paybackYears,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    submittedAt: new Date().toISOString(),
    leadSource: "solar-savings-calculator",
    stage: "completed",
    ...trafficFields(),
  };
  await fetch(WEBHOOK_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** Acquisition source (Google, Bing, Facebook, Direct...), not the form's page. */
function trafficFields() {
  const t = getTrafficSource();
  return {
    source: t.source,
    source_medium: t.medium,
    source_campaign: t.campaign,
    source_term: t.term,
    source_content: t.content,
    source_referrer: t.referrer,
    landing_page: t.landingPage,
    source_page:
      typeof window !== "undefined"
        ? window.location.pathname
        : "/solar-savings-calculator",
  };
}


/**
 * Fired as soon as the customer confirms their details on the personalised
 * link's first screen, so the team has the lead even if they drop out later.
 */
async function sendPrefillConfirmWebhook(value: ConfirmedPrefill): Promise<void> {
  const payload = {
    firstName: value.name ? (value.name.split(" ")[0] ?? value.name) : "",
    fullName: value.name ?? "",
    phone: value.phone ?? "",
    email: value.email ?? "",
    postcode: value.postcode ?? "",
    region: regionForPostcode(value.postcode ?? ""),
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    submittedAt: new Date().toISOString(),
    leadSource: "solar-savings-calculator",
    stage: "details-confirmed",
    ...trafficFields(),
  };
  await fetch(WEBHOOK_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}


